import { db, OpRow, OpStatus } from './db';

/** Unique client id for a queued op (also the temp entityId for creates). */
export function newOpId(): string {
  const c = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Max server-rejected ('error') push attempts before an op is parked terminally. */
export const MAX_OP_RETRIES = 5;

/**
 * Ops eligible for a push attempt: fresh 'pending' ops PLUS server-rejected
 * 'error' ops that haven't exhausted their bounded retries (so a transient
 * server error self-heals; a permanent one parks terminally + stays visible).
 */
export async function drainableOps(): Promise<OpRow[]> {
  return db.opQueue
    .filter((o) => o.status === 'pending' || (o.status === 'error' && (o.retries || 0) < MAX_OP_RETRIES))
    .toArray();
}

/** Server-rejected ops (visible to the user as "failed"). */
export async function errorCount(): Promise<number> {
  return db.opQueue.where('status').equals('error').count();
}

export async function opsByStatus(status: OpStatus): Promise<OpRow[]> {
  return db.opQueue.where('status').equals(status).toArray();
}

export async function pendingCount(): Promise<number> {
  return db.opQueue.where('status').equals('pending').count();
}

/**
 * Every un-synced op the replica wipe (clearReplica) would destroy — pending
 * PLUS parked conflict/error ops, which hold un-pushed payloads recoverable via
 * the review UI. The confirm-on-logout guard must count THIS (not pendingCount
 * alone), so it warns about all work that logout discards, not just fresh ops.
 */
export async function unsyncedOpCount(): Promise<number> {
  return db.opQueue.count();
}

export async function conflictOps(): Promise<OpRow[]> {
  return opsByStatus('conflict');
}

export async function errorOps(): Promise<OpRow[]> {
  return opsByStatus('error');
}

/** All ops needing manual attention (conflict + error), for the review UI. */
export async function reviewOps(): Promise<OpRow[]> {
  return [...(await conflictOps()), ...(await errorOps())];
}

/**
 * B8: when a not-yet-synced parent CREATE is discarded/cancelled, remove the
 * queued CHILD CREATE ops that reference its temp id (and their optimistic
 * rows) — otherwise a child would drain to /sync/push pointing at a parent that
 * never existed server-side (dangling FK / garbage row). Only child CREATES can
 * reference a never-synced temp parent (an update folds into the create; a
 * delete cancels it), so we restrict to creates. One level deep — no grandchild
 * offline chain is wired today.
 *
 * MUST run inside a transaction whose scope already covers opQueue + the child
 * tables (callers open a wide ALL_TABLES tx). Reference detection mirrors the
 * server's own temp-id remap (a substring match on the payload JSON), so client
 * and server agree on what "references the temp id" means.
 */
export async function cascadeDiscardChildren(parentTempId: string): Promise<void> {
  const children = (await db.opQueue.toArray()).filter(
    (o) =>
      o.operationType === 'create' &&
      o.entityId !== parentTempId &&
      JSON.stringify(o.payload ?? {}).includes(parentTempId),
  );
  for (const child of children) {
    await db.opQueue.delete(child.id);
    await (db as any)[child.table].delete(child.entityId);
  }
}

/**
 * How many queued CHILD CREATE ops would be discarded along with this parent
 * create (same reference rule as cascadeDiscardChildren) — so the review UI can
 * disclose the blast radius before a Discard.
 */
export async function countCascadeChildren(parentTempId: string): Promise<number> {
  return (await db.opQueue.toArray()).filter(
    (o) =>
      o.operationType === 'create' &&
      o.entityId !== parentTempId &&
      JSON.stringify(o.payload ?? {}).includes(parentTempId),
  ).length;
}

/**
 * When a parent CREATE succeeds and the server assigns a real id, re-point every
 * OTHER queued op (and its optimistic replica row) that referenced the parent's
 * temp id to the server id. The server already does this for ops LATER IN THE
 * SAME push batch (its in-batch idMap), but that map is rebuilt per batch and
 * never reaches a child whose own op errored/parked: that child would keep a
 * now-dead temp FK, FK-fail on every retry, and strand the clinical record
 * forever. Mirroring the remap client-side on parent-success closes that gap and
 * survives a parent-success / child-error split.
 *
 * Detection + rewrite mirror the server exactly (substring on the payload JSON,
 * `split(tempId).join(serverId)`), so both sides agree on what "references the
 * temp id" means. Also re-points the child's optimistic replica row FK (one
 * level deep) so chart reads — which filter strictly on the FK — don't drop a
 * just-created child once the parent row swaps to its server id.
 *
 * MUST run inside a transaction whose scope covers opQueue + all child tables
 * (callers open an ALL_TABLES tx).
 */
export async function remapTempIdReferences(tempId: string, serverId: string): Promise<void> {
  if (!tempId || !serverId || tempId === serverId) return;
  const ops = await db.opQueue.toArray();
  for (const o of ops) {
    if (o.entityId === tempId) continue; // the parent op itself
    const json = JSON.stringify(o.payload ?? {});
    if (!json.includes(tempId)) continue;
    await db.opQueue.update(o.id, { payload: JSON.parse(json.split(tempId).join(serverId)) });
    const childTbl = (db as any)[o.table];
    if (!childTbl) continue;
    const row = await childTbl.get(o.entityId);
    if (row) await childTbl.put(JSON.parse(JSON.stringify(row).split(tempId).join(serverId)));
  }
}
